import React, { useEffect, useState } from 'react'
import { adminRequest } from '../api/client'
import { Card, EmptyState, PageTitle, StatusBadge, formatDate } from '../components/Ui'
import { BackLink, DetailGrid, SummaryGrid, Timeline } from '../components/AdminCommon'

function usd(value) {
  return `$${Number(value || 0).toFixed(4)}`
}

export function AiRuns({ navigate }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  useEffect(() => {
    let disposed = false
    adminRequest('/api/v1/admin/ai/runs')
      .then((response) => { if (!disposed) setItems(response?.data || []) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || 'AI 실행 이력을 불러오지 못했습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [])

  return <><PageTitle eyebrow="AI RUN LOG · LIVE DB" title="AI 실행 이력" description="difan_dev ai_runs에 실제 저장된 실행 단위를 추적합니다." /><Card className="table-card">{loading ? <p className="help-copy">AI 실행 이력을 불러오는 중입니다.</p> : error ? <EmptyState title="AI 실행 이력 조회 실패" description={error} /> : items.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>실행 ID</th><th>기능</th><th>대상</th><th>모델</th><th>Prompt</th><th>토큰</th><th>비용</th><th>지연</th><th>상태</th><th>시각</th></tr></thead><tbody>{items.map((item) => <tr className="clickable-row" key={item.id} onClick={() => navigate(`/ai/runs/${item.id}`)}><td><small className="mono">{item.id}</small></td><td><strong>{item.feature}</strong></td><td>{item.subjectType || '-'}<small>{item.subjectId || '-'}</small></td><td><small className="mono">{item.modelVersion || '-'}</small></td><td>{item.promptVersion || '-'}</td><td>{Number(item.tokenCount || 0).toLocaleString()}</td><td>{usd(item.cost)}</td><td>{Number(item.latencyMs || 0).toLocaleString()}ms</td><td><StatusBadge status={item.status} /></td><td>{formatDate(item.createdAt, true)}</td></tr>)}</tbody></table></div> : <EmptyState title="저장된 AI 실행이 없습니다." description="실제 AI 기능이 실행되면 ai_runs 기록이 표시됩니다." />}</Card></>
}

export function AiRunDetail({ id, navigate }) {
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  useEffect(() => {
    let disposed = false
    adminRequest(`/api/v1/admin/ai/runs/${encodeURIComponent(id)}`)
      .then((response) => { if (!disposed) setItem(response?.data || null) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || 'AI 실행을 찾을 수 없습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [id])

  if (loading) return <><BackLink onClick={() => navigate('/ai/runs')} /><Card><p>실제 AI 실행 원장을 조회하는 중입니다.</p></Card></>
  if (error || !item) return <><BackLink onClick={() => navigate('/ai/runs')} /><Card><EmptyState title="AI 실행 조회 실패" description={error || 'AI 실행을 찾을 수 없습니다.'} /></Card></>

  const analyses = item.analyses || []
  return <>
    <BackLink onClick={() => navigate('/ai/runs')} />
    <PageTitle eyebrow="AI RUN DETAIL · LIVE DB" title={item.id} description="모델·Prompt·설정 버전과 연결된 견적 분석을 실제 DB에서 확인합니다." />
    <SummaryGrid items={[
      { label: '상태', value: <StatusBadge status={item.status} /> },
      { label: '기능', value: item.feature },
      { label: '비용', value: usd(item.cost) },
      { label: '지연', value: `${Number(item.latencyMs || 0).toLocaleString()}ms` },
    ]} />
    <div className="dashboard-grid half">
      <Card title="실행 식별 정보"><DetailGrid items={[
        { label: '대상 유형', value: item.subjectType || '-' },
        { label: '대상 ID', value: item.subjectId || '-', wide: true },
        { label: '모델 Snapshot', value: item.modelVersion || '-' },
        { label: 'Prompt Version', value: item.promptVersion || '-' },
        { label: 'Config Version', value: item.configVersion || '-' },
        { label: '실행 시각', value: formatDate(item.createdAt, true) },
        { label: '완료 시각', value: formatDate(item.completedAt, true) },
      ]} /></Card>
      <Card title="사용량·오류"><DetailGrid items={[
        { label: 'Token count', value: Number(item.tokenCount || 0).toLocaleString() },
        { label: '저장 비용', value: usd(item.cost) },
        { label: 'Latency', value: `${Number(item.latencyMs || 0).toLocaleString()}ms` },
        { label: 'Error code', value: item.errorCode || '-' },
      ]} /></Card>
    </div>
    <Card title="연결된 견적 분석" description="이 ai_run을 참조하는 quote_request_ai_analyses입니다.">
      {analyses.length ? <div className="table-scroll"><table className="data-table compact"><thead><tr><th>분석 ID</th><th>서비스</th><th>회원 ID</th><th>신뢰도</th><th>추천 수</th><th>생성일</th></tr></thead><tbody>{analyses.map((analysis) => <tr key={analysis.id}><td><small className="mono">{analysis.id}</small></td><td>{analysis.serviceName || '-'}</td><td><small className="mono">{analysis.memberId}</small></td><td>{(Number(analysis.confidence || 0) * 100).toFixed(1)}%</td><td>{Number(analysis.recommendationCount || 0)}명</td><td>{formatDate(analysis.createdAt, true)}</td></tr>)}</tbody></table></div> : <EmptyState title="연결된 견적 분석이 없습니다." />}
    </Card>
    <Card title="실제 실행 이력"><Timeline items={[
      { label: 'AI 실행 생성', detail: `${item.feature} · ${item.modelVersion || '-'}`, at: formatDate(item.createdAt, true) },
      { label: item.status === 'FAILED' ? '실패 기록' : '실행 완료', detail: item.status === 'FAILED' ? (item.errorCode || '실패') : `${Number(item.tokenCount || 0).toLocaleString()} tokens · ${usd(item.cost)}`, at: formatDate(item.completedAt || item.createdAt, true) },
    ]} /></Card>
  </>
}
